import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { GatewaySpinner } from '@/_shared/components/GatewayShell';
import { TruncateWithTooltip } from '@/_shared/components/TruncateWithTooltip';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { usePendingInvites } from '@/_shared/hooks/usePendingInvites';
import { orpc } from '@/_shared/lib/orpc';
import type { client } from '@/_shared/lib/rpc';

import { CreateWorkspaceDialog } from './CreateWorkspaceDialog';
import { CreatedByLine, MEMBERSHIP_SUSPENDED_MESSAGE, RegionBadge } from './PickerParts';

type SiblingPicker = Awaited<ReturnType<typeof client.workspace.listSiblingPicker>>;
type PickerOrganization = SiblingPicker['organizations'][number];
type JoinableWorkspace = SiblingPicker['joinable'][number];

const SIBLING_PICKER_KEY = orpc.workspace.listSiblingPicker.key();

const NOT_RUDDERSTACK_ADMIN_MESSAGE = 'Only RudderStack admins of this organization can create a Lookout workspace.';
const OPEN_SOURCE_MESSAGE = 'Lookout workspaces are not available on the open-source plan.';
const NO_ACCESS_MANAGEMENT_MESSAGE = 'This organization does not use Access Management. Lookout workspaces need it.';
const JOIN_NOT_ALLOWED_MESSAGE =
  'You cannot join this workspace now. Ask a RudderStack admin of the organization to add you to it.';
const ACCESS_CHECK_FAILED_MESSAGE =
  'Lookout cannot check your RudderStack access right now. Try again in a few minutes.';
const RUDDERSTACK_UNAVAILABLE_MESSAGE = 'RudderStack did not answer. Try again.';

/** The tooltip and refusal text of the Lookout workspace limit (ACT2-1124 LLD §5.1). */
function lookoutWorkspaceLimitMessage(limit: number | null): string {
  if (limit === null) return 'Your plan does not allow more Lookout workspaces in this organization.';
  return `Your plan allows ${limit} Lookout ${limit === 1 ? 'workspace' : 'workspaces'} in this organization.`;
}

/** Why Create is disabled for the organization, or null when it is enabled. */
function blockedReason(organization: PickerOrganization): string | null {
  switch (organization.blockedBy) {
    case 'open_source_plan':
      return OPEN_SOURCE_MESSAGE;
    case 'no_access_management':
      return NO_ACCESS_MANAGEMENT_MESSAGE;
    case 'lookout_workspace_limit_reached':
      return lookoutWorkspaceLimitMessage(organization.lookoutWorkspacesLimit);
    case null:
      return null;
  }
}

/** The limit that a create refusal names, when the server sends one. */
function limitOf(err: Error): number | null {
  const data = (err as Error & { data?: unknown }).data;
  if (data !== null && typeof data === 'object' && 'limit' in data && typeof data.limit === 'number') return data.limit;
  return null;
}

function isUpstreamFailure(message: string): boolean {
  return message.includes('rudderstack_unavailable') || message.includes('rudderstack_forbidden');
}

/**
 * The picker of the sibling model (ACT2-1124 LLD §5.1, §5.4), shown while the Lookout flag is on: the sibling
 * workspaces that the person can join, and the organizations where they can create one, in US or EU.
 */
export function SiblingWorkspacePicker({
  reauth,
  onCreated,
  onJoined,
}: {
  /** The picker's RudderStack re-auth: a missing or refused ID token signs the person in again. */
  reauth: { isLoading: boolean; start: () => void };
  onCreated: (workspaceId: string) => Promise<void>;
  onJoined: (workspaceId: string) => Promise<void>;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const pickerQuery = useQuery(orpc.workspace.listSiblingPicker.queryOptions());
  const invitesQuery = usePendingInvites();
  /** The organization of the open create dialog. */
  const [createForId, setCreateForId] = useState<string | null>(null);

  const reauthNeeded = pickerQuery.error?.message.includes('rudderstack_reauth_required') === true;
  const reauthStartedRef = useRef(false);
  useEffect(() => {
    // The provider probe decides the path; the native one signs out, so it must run once.
    if (!reauthNeeded || reauth.isLoading || reauthStartedRef.current) return;
    reauthStartedRef.current = true;
    reauth.start();
  }, [reauthNeeded, reauth]);

  const refreshPicker = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: SIBLING_PICKER_KEY });
  };

  const createMutation = useMutation(
    orpc.workspace.createSibling.mutationOptions({
      onSuccess: async (data) => {
        await onCreated(data.workspace.id);
      },
      onError: async (err: Error, vars) => {
        const message = err.message;
        if (message.includes('rudderstack_reauth_required')) {
          reauth.start();
          return;
        }
        const organization = pickerQuery.data?.organizations.find((o) => o.id === vars.organizationId);
        const collapse = (): void => setCreateForId((id) => (id === vars.organizationId ? null : id));
        if (message.includes('lookout_workspace_limit_reached')) {
          collapse();
          await refreshPicker();
          toast.error(lookoutWorkspaceLimitMessage(limitOf(err) ?? organization?.lookoutWorkspacesLimit ?? null));
          return;
        }
        if (message.includes('not_organization_admin')) {
          collapse();
          await refreshPicker();
          toast.error(NOT_RUDDERSTACK_ADMIN_MESSAGE);
          return;
        }
        if (message.includes('open_source_plan') || message.includes('no_access_management')) {
          collapse();
          await refreshPicker();
          toast.error(message.includes('open_source_plan') ? OPEN_SOURCE_MESSAGE : NO_ACCESS_MANAGEMENT_MESSAGE);
          return;
        }
        if (message.includes('already taken')) {
          // The URL of an unfinished create can be gone now, so the form can show the URL to pick.
          await refreshPicker();
          toast.error('This workspace URL is already taken. Pick another one.');
          return;
        }
        if (isUpstreamFailure(message)) {
          // A create with no answer keeps its URL for the next try, which the refreshed list shows.
          await refreshPicker();
          toast.error(
            message.includes('rudderstack_unavailable')
              ? RUDDERSTACK_UNAVAILABLE_MESSAGE
              : 'Unable to create workspace. Try again.',
          );
          return;
        }
        if (message.includes('sibling_create_')) {
          toast.error('RudderStack refused to create this workspace. Contact customer support.');
          return;
        }
        toast.error(message || 'Could not create the workspace. Please try again.');
      },
    }),
  );

  const joinMutation = useMutation(
    orpc.workspace.joinSibling.mutationOptions({
      onSuccess: async (data) => {
        await onJoined(data.workspace.id);
      },
      onError: async (err: Error) => {
        const message = err.message;
        if (message.includes('rudderstack_reauth_required')) {
          reauth.start();
          return;
        }
        if (message.includes('sibling_join_not_allowed')) {
          await refreshPicker();
          toast.error(JOIN_NOT_ALLOWED_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_membership_suspended')) {
          await refreshPicker();
          toast.error(MEMBERSHIP_SUSPENDED_MESSAGE);
          return;
        }
        if (message.includes('rudderstack_access_check_failed')) {
          toast.error(ACCESS_CHECK_FAILED_MESSAGE);
          return;
        }
        if (isUpstreamFailure(message)) {
          toast.error('Unable to join workspace. Try again.');
          return;
        }
        toast.error(message || 'Could not join the workspace. Please try again.');
      },
    }),
  );

  if (pickerQuery.isPending || reauthNeeded) {
    return <GatewaySpinner mode="inline" label="Loading workspaces" />;
  }
  if (pickerQuery.isError) {
    return (
      <div className="mt-6 space-y-3">
        <Callout variant="error" className="text-left">
          Could not load your RudderStack organizations.
        </Callout>
        <Button variant="secondary" size="md" onClick={() => void pickerQuery.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { organizations, memberListUnavailable } = pickerQuery.data;
  // A workspace with a live invite shows only its invite card (PendingInvites, above this picker), so that the invite's
  // role applies. The server would accept the invite on Join anyway.
  const invitedWorkspaceIds = new Set((invitesQuery.data?.invites ?? []).map((invite) => invite.workspaceId));
  const joinable = pickerQuery.data.joinable.filter((workspace) => !invitedWorkspaceIds.has(workspace.workspaceId));

  const renderJoinCard = (workspace: JoinableWorkspace): React.ReactElement => (
    <li key={workspace.workspaceId} className="rounded-xl border border-border">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-3 px-4 py-3">
        <div className="flex max-w-full min-w-0 shrink-0 flex-col gap-1 text-left">
          <div className="flex min-w-0 items-center gap-2">
            <TruncateWithTooltip className="text-sm font-medium text-foreground">{workspace.name}</TruncateWithTooltip>
            {workspace.region && <RegionBadge region={workspace.region} />}
          </div>
          <p className="flex min-w-0 items-baseline gap-1 text-xs text-muted-foreground">
            <span className="shrink-0">In</span>
            <TruncateWithTooltip className="font-medium text-foreground">
              {workspace.organizationName ?? workspace.organizationId}
            </TruncateWithTooltip>
          </p>
          <CreatedByLine target={workspace} />
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <Button
            variant="primary"
            size="md"
            aria-label={`Join ${workspace.name}`}
            loading={joinMutation.isPending && joinMutation.variables?.workspaceId === workspace.workspaceId}
            disabled={joinMutation.isPending}
            onClick={() => joinMutation.mutate({ workspaceId: workspace.workspaceId })}
          >
            Join
          </Button>
        </div>
      </div>
    </li>
  );

  // A refreshed list can block the organization; then the dialog closes.
  const createFor = organizations.find((o) => o.id === createForId && blockedReason(o) === null) ?? null;

  const renderCreateCard = (organization: PickerOrganization): React.ReactElement => {
    const reason = blockedReason(organization);
    const label = organization.name ?? organization.id;
    return (
      <li key={organization.id} className="rounded-xl border border-border">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-3 px-4 py-3">
          <div className="flex max-w-full min-w-0 shrink-0 items-center gap-2 text-left">
            <TruncateWithTooltip className="text-sm font-medium text-foreground">{label}</TruncateWithTooltip>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <ActionTooltip show={reason !== null} message={reason ?? ''}>
              <Button
                variant="secondary"
                size="md"
                aria-label={`Create a workspace in ${label}`}
                disabled={reason !== null}
                aria-haspopup="dialog"
                onClick={() => setCreateForId(organization.id)}
              >
                Create
              </Button>
            </ActionTooltip>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div className="mt-6 space-y-5 text-left">
      {memberListUnavailable && (
        <Callout variant="warning" className="text-left">
          {`${ACCESS_CHECK_FAILED_MESSAGE} Some workspaces may be missing from this list.`}
        </Callout>
      )}
      {joinable.length === 0 && organizations.length === 0 && invitedWorkspaceIds.size === 0 ? (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Building2 />
            </EmptyMedia>
            <EmptyTitle>No workspaces to create or join</EmptyTitle>
            <EmptyDescription>
              Only RudderStack admins create Lookout workspaces. Ask a RudderStack admin of your organization to create
              one or to add you to one, then refresh.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <>
          {joinable.length > 0 && (
            <section>
              <h2 className="px-1 text-xs font-bold tracking-wider text-muted-foreground uppercase">Join your team</h2>
              <p className="mt-1 mb-2 px-1 text-xs text-muted-foreground">
                Joining keeps everyone's chats and dashboards together.
              </p>
              <ul className="space-y-2.5">{joinable.map(renderJoinCard)}</ul>
            </section>
          )}
          {organizations.length > 0 && (
            <section>
              <h2 className="px-1 text-xs font-bold tracking-wider text-muted-foreground uppercase">
                Create a workspace
              </h2>
              <p className="mt-1 mb-2 px-1 text-xs text-muted-foreground">
                Pick a RudderStack organization where you are an admin.
              </p>
              <ul className="space-y-2.5">{organizations.map(renderCreateCard)}</ul>
            </section>
          )}
        </>
      )}
      <CreateWorkspaceDialog
        organization={createFor}
        pending={createMutation.isPending}
        onCreate={(input) => createMutation.mutate(input)}
        onClose={() => setCreateForId(null)}
      />
    </div>
  );
}
